<template>
    <div>
        <v-container grid-list-xs>
            <v-row>
                <v-col>
                    <v-sheet class="pa-5" elevation="3">
                        <PageHeader>
                            <template #header>
                                <h4>{{ $t("plan.review-selection-header") }}</h4>
                            </template>
                            <template #subheader>
                                {{ $t(`plan.review-selection-subheader`) }}
                            </template>
                            <template #main>
                                <div class="mt-6">Your Selected Offer</div>
                            </template>
                        </PageHeader>

                        <v-radio-group hide-details :value="paymentPlan">
                            <PaymentPlanOption
                                v-if="paymentPlan"
                                :key="paymentPlan.providerPlanCode"
                                :payment-plan="paymentPlan"
                                :down-payment-required="downPaymentRequired"
                                :financed-amount-cents="tentativePurchaseAmountCents"
                                :provider-key="paymentPlan.providerKey"
                            />
                        </v-radio-group>

                        <div>
                            <div class="cost-box">
                                <div>
                                    <v-icon class="tool-tip">$vuetify.icons.exclamationCircle</v-icon>
                                </div>
                                <div>
                                    <div class="banner-text">
                                        <p class="primary-color">
                                            Here is the offer you selected to use if approved for a CareCredit credit
                                            card. You can change your selection later at checkout.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div id="credit-body" class="text-center mt-5">
                                <div class="font-weight-bold">
                                    You have been prequalified for <FormattedAmount :amount="approvedAmountCents" />. The amount of your
                                    treatment is
                                    <FormattedAmount :amount="tentativePurchaseAmountCents" :include-cents="true" />.
                                </div>
                                <div>
                                    If you would like to finance an amount different than the amount of your treatment,
                                    please go back to the previous page to make any changes.
                                </div>
                            </div>
                        </div>

                        <div>
                            If there are no more changes, click "Continue" to securely complete your loan application on
                            Synchrony's website
                        </div>

                        <div class="card-actions pb-12 mt-8">
                            <v-btn class="card-actions-back" rounded outlined color="primary" @click="back()">
                                {{ $t("action.back") }}
                            </v-btn>
                            <Button id="continueButton" class="card-actions-submit secondary" @click="submit">
                                {{ continueButtonVerbiage }}
                            </Button>
                        </div>
                    </v-sheet>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
    import { applicationState, providerState, sessionState } from "@/state"
    import PaymentPlanOption from "@/components/paymentplan/PaymentPlanOption.vue"
    import Providers from "@/model/provider/Providers"
    import ApplyMode from "@/model/apply/ApplyMode"
    import { workflowService } from "@/service"
    import SelectPaymentPlanAction from "@/action/actions/SelectPaymentPlanAction"

    export default {
        components: {
            PaymentPlanOption,
        },

        data() {
            const application = applicationState.application
            const paymentPlan = applicationState.selectedPaymentPlan

            let response = null

            if (applicationState.applyMode !== ApplyMode.FULL) {
                response = providerState.getPreAuthResponse()
            }
            if (applicationState.applyMode === ApplyMode.FULL) {
                response = providerState.getApplyResponse()
            }

            let approvedAmountCents = response.approvedAmountCents

            return {
                application: application,
                downPaymentRequired: response?.downPaymentRequired,
                creditTerm: paymentPlan?.durationMonths,
                financedAmountCents: paymentPlan?.financedAmountCents,
                totalCostCents: paymentPlan?.totalCostCents,
                approvedAmountCents: approvedAmountCents,
                paymentPlan,
                providerInfo: Providers[sessionState.dynamicProviderKey.providerKey],
                sendEmail: false,
                sendMobile: false,
                submitting: false,
                tentativePurchaseAmountCents: application.tentativePurchaseAmountCents,
            }
        },

        computed: {
            continueButtonVerbiage() {
                if (applicationState.applyMode === ApplyMode.FULL) return this.$t("action.continue-and-finalize")
                return this.$t("action.continue-to-apply")
            },

            initialPayment() {
                return (
                    this.paymentPlan.downPaymentCents !== undefined || this.paymentPlan.downPaymentPercent !== undefined
                )
            },
        },

        methods: {
            back() {
                applicationState.selectedPaymentPlan = null
                this.$router.push(`/providers/${sessionState.providerKey}/paymentPlans`)
            },

            async submit() {
                try {
                    this.submitting = true
                    applicationState.application.purchaseAmountCents =
                        applicationState.application.tentativePurchaseAmountCents
                    await workflowService.invoke(
                        new SelectPaymentPlanAction(
                            this.paymentPlan,
                            applicationState.application.purchaseAmountCents,
                            false,
                            this.providerInfo.selectPaymentPlanOnReviewScreen
                        )
                    )
                    await this.$router.push(
                        `/providers/${sessionState.providerKey}/modes/${applicationState.applyMode}/offerReviewNext`
                    )
                } finally {
                    this.submitting = false
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    p {
        color: $primary-color;
    }

    td {
        max-width: 250px;
    }

    table tr {
        border: 1px solid red !important;
    }

    .table-font {
        font-size: to_rem(16px) !important;
    }

    .v-data-table td {
        border-bottom: 1px lightgrey solid;
        padding: 10px 10px 10px 24px !important;

        &:first-child {
            width: 40%;
        }

        &:nth-child(1) {
            border-bottom: none;
        }

        &:last-child {
            border-bottom: none;
        }
    }

    .no-left-border {
        border-left: none !important;
    }

    .no-right-border {
        border-right: none !important;
    }

    .v-input__control {
        margin-right: -10px !important;
    }

    .payment-container {
        background-color: $bg-color-neutral;
        border-radius: 8px;
        margin-top: 20px;
        padding-left: 20px;
    }

    .payment-container-nobg {
        margin-top: 20px;
        padding-left: 20px;
    }

    @media only screen and (max-width: 600px) {
        .v-data-table td {
            &:first-child {
                width: 60%;
            }

            &:nth-child(2) {
                text-align: left;
            }
        }
    }
    .cost-box {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        text-align: left;
        padding: 5px;
        height: auto;
        background-color: rgba(231, 242, 254);
        border-radius: 8px;
        border: 1px solid #002855;
        margin-top: 1rem;
        .tool-tip {
            width: 100%;
            padding-right: 10px;
        }

        .banner-text {
            margin-top: 1rem;
            margin-left: 0;
            font-size: 16px;
            line-height: 10px;
        }
    }
</style>
